import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { Money } from '@/graphql/saleor/api';
import { useRegionProvider } from '@/providers/RegionProvider';
import { messages as financeCalculatorModalMessages } from '@/views/WatchView/components/FinanceCalculatorModal/messages';

import * as S from './FinanceCalculator.styles';
import { messages } from './messages';
export interface CreditPlusFinanceCalculatorProps {
  className?: string;
  colNum?: number;
  monthlyLoanTerm?: number;
  price: Money;
  selectMonthlyLoanTerm?: (value: number) => void;
}
export const CreditPlusFinanceCalculator = ({
  className,
  colNum = 4,
  monthlyLoanTerm: _monthlyLoanTerm,
  price,
  selectMonthlyLoanTerm,
}: CreditPlusFinanceCalculatorProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [monthlyLoanTerm, setMonthlyLoanTerm] = useState(
    _monthlyLoanTerm ?? 12,
  );
  const months = [6, 12, 18, 20, 24, 36, 48, 60];

  const handleMonthlyLoanTerm = (value: number) => {
    setMonthlyLoanTerm(value);
    selectMonthlyLoanTerm && selectMonthlyLoanTerm(value);
  };

  const monthlyPayments = useMemo(() => {
    return +(price.amount / monthlyLoanTerm).toFixed(2);
  }, [monthlyLoanTerm]);

  return (
    <S.Container
      className={className}
      data-test-id='CreditPlusFinanceCalculator-Container'
    >
      <Typography>
        {formatMessage(messages.creditPlusAdvantagesText)}
      </Typography>
      <S.BenefitList>
        <li>
          <Typography>
            {formatMessage(messages.easyOnlineApplicationProcess)}
          </Typography>
        </li>
        <li>
          <Typography>
            {formatMessage(messages.smoothOnlineIdentificationVideoIdent)}
          </Typography>
        </li>
        <li>
          <Typography>{formatMessage(messages.zeroApr)}</Typography>
        </li>
        <li>
          <Typography>{formatMessage(messages.noHiddenFees)}</Typography>
        </li>
      </S.BenefitList>
      <Typography
        fontWeight='bold'
        textAlign='center'
        textTransform='uppercase'
      >
        {formatMessage(financeCalculatorModalMessages.selectTermDuration)}
      </Typography>
      <Typography marginBottom='s100' textAlign='center'>
        {formatMessage(financeCalculatorModalMessages.currentSelection)}
        {monthlyLoanTerm} {formatMessage(financeCalculatorModalMessages.months)}
      </Typography>

      <S.TermsGrid $colNum={colNum}>
        {months.map((el) => (
          <S.TermButton
            key={el}
            data-testid={`creditPlusFinanceCalculatorTerm${el}`}
            isMobile={false}
            isSelected={el === monthlyLoanTerm}
            onClick={() => handleMonthlyLoanTerm(el)}
          >
            <Typography>{el}</Typography>
            <Typography>{formatMessage(messages.months)}</Typography>
          </S.TermButton>
        ))}
      </S.TermsGrid>

      <S.MonthlyPaymentsBox
        data-test-id='creditPlusMonthlyPaymentBox'
        marginBottom='s100'
        marginTop='s100'
      >
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.yourMonthlyPayments)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {formatPrice(
              {
                amount: monthlyPayments,
                currency: price.currency,
              },
              {
                currencyDisplay: 'symbol',
              },
            )}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.netLoanAmount)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {formatPrice(price, {
              currencyDisplay: 'symbol',
            })}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.monthlyLoanTerm)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {monthlyLoanTerm} {formatMessage(messages.months)}
          </Typography>
        </Flex>
        <Flex justifyContent='space-between'>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.yourProvider)}
          </Typography>
          <Typography scale='p0' textTransform='uppercase'>
            {formatMessage(messages.creditplusBank)}
          </Typography>
        </Flex>
      </S.MonthlyPaymentsBox>

      <Typography marginTop='s150' scale='p3'>
        {formatMessage(
          financeCalculatorModalMessages.creditPlusAdditionalText1,
        )}
      </Typography>
      <Typography marginBottom='s150' marginTop='s150' scale='p3'>
        {formatMessage(
          financeCalculatorModalMessages.creditPlusAdditionalText2,
        )}
      </Typography>
    </S.Container>
  );
};
